
// components/form/UserForm.tsx
import React, { useState, useEffect, ChangeEvent, FormEvent, DragEvent } from 'react';
import { Person } from 'types';

interface UserFormProps {
  userData?: Person | null;
  onClose: () => void;
  onSubmit: (data: Person, isEditMode: boolean) => void;
  loading?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({ 
  userData = null, 
  onClose, 
  onSubmit, 
  loading = false ,

}) => {
  const [formData, setFormData] = useState<Person>({
    full_name: "",
    id_number: "",
    id_file: null,
    gender: "",
    age: "",
    phone: "",
    email: "",
    region: "",
    zone: "",
    subcity: "",
    woreda: "",
    town: "",
    house_number: "",
    fayda_number: ""
  });
  
  const [fileError, setFileError] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Check if we're editing an existing user
  const isEditMode = !!userData;

  useEffect(() => {
    if (userData) {
      setFormData(userData);
    }
  }, [userData]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setFileError("Please upload a PDF file");
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setFileError("File size must be less than 10MB");
        return;
      }
      setFileError("");
      setFormData(prev => ({
        ...prev,
        id_file: file
      }));
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const inputEvent = {
        target: {
          files: [file]
        }
      } as unknown as ChangeEvent<HTMLInputElement>;
      handleFileChange(inputEvent);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    // Basic validation
    if (!formData.full_name || !formData.id_number || !formData.email) {
      setError("Please fill in all required fields");
      return;
    }

    if (onSubmit) {
      onSubmit(formData, isEditMode);
    }
  };

  const getFileName = (): string => {
    if (formData.id_file) {
      return formData.id_file.name || "ID Document uploaded";
    }
    return "";
  };

  const getFileSize = (): string => {
    if (formData.id_file && formData.id_file.size) {
      return `${(formData.id_file.size / (1024 * 1024)).toFixed(2)} MB`;
    }
    return "";
  };

  // If in edit mode (person exists), display as read-only
  if (isEditMode) {
    return (
      <div className="p-4 border rounded-lg bg-gray-50">
        <h3 className="font-semibold text-gray-800 mb-4">Person Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Full Name</label>
            <p className="mt-1 p-2 bg-white border rounded">{formData.full_name}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">ID Number</label>
            <p className="mt-1 p-2 bg-white border rounded">{formData.id_number}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Gender</label>
            <p className="mt-1 p-2 bg-white border rounded">{formData.gender === 'M' ? 'Male' : formData.gender === 'F' ? 'Female' : '-'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Age</label>
            <p className="mt-1 p-2 bg-white border rounded">{formData.age || '-'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Phone</label>
            <p className="mt-1 p-2 bg-white border rounded">{formData.phone || '-'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Email</label>
            <p className="mt-1 p-2 bg-white border rounded">{formData.email || '-'}</p>
          </div>
        </div>
      </div>
    );
  }

  // Create mode - show form with inputs
  return (
    <div className="p-4 border rounded-lg bg-white">
      <div className="form-header flex justify-between items-center mb-4">
        <div>
          <p className="referral-popup-title">Create New User</p>
          <p className="description">Add new user information</p>
        </div>
      </div>
      
      {error && <div className="error-message text-red-400 mb-4">{error}</div>}
      
      <form onSubmit={handleSubmit}>
        {/* Personal Information */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="flex flex-col">
            <label className="referral-popup-label">Full Name *</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="full_name"
              placeholder="Full Name"
              value={formData.full_name}
              required
            />
          </div>
          
          <div className="flex flex-col">
            <label className="referral-popup-label">ID Number *</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="id_number"
              placeholder="ID Number"
              value={formData.id_number}
              required
            />
          </div>
          
          <div className="flex flex-col">
            <label className="referral-popup-label">Gender</label>
            <select
              className="referral-form-field"
              onChange={handleInputChange}
              name="gender"
              value={formData.gender}
            >
              <option value="">Select Gender</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="flex flex-col">
            <label className="referral-popup-label">Age</label>
            <input
              type="number"
              className="referral-form-field"
              onChange={handleInputChange}
              name="age"
              placeholder="Age"
              value={formData.age}
            />
          </div>
        
          <div className="flex flex-col">
            <label className="referral-popup-label">Phone</label>
            <input
              type="tel"
              className="referral-form-field"
              onChange={handleInputChange}
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
            />
          </div>
          
          <div className="flex flex-col">
            <label className="referral-popup-label">Email *</label>
            <input
              type="email"
              className="referral-form-field"
              onChange={handleInputChange}
              name="email"
              placeholder="Email"
              value={formData.email}
              required
            />
          </div>
        </div>

        {/* Address Information */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="flex flex-col">
            <label className="referral-popup-label">Region</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="region"
              placeholder="Region"
              value={formData.region}
            />
          </div>
          
          <div className="flex flex-col">
            <label className="referral-popup-label">Zone</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="zone"
              placeholder="Zone"
              value={formData.zone}
            />
          </div>
          
          <div className="flex flex-col">
            <label className="referral-popup-label">Subcity</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="subcity"
              placeholder="Subcity"
              value={formData.subcity}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="flex flex-col">
            <label className="referral-popup-label">Woreda</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="woreda"
              placeholder="Woreda"
              value={formData.woreda}
            />
          </div>

          <div className="flex flex-col">
            <label className="referral-popup-label">Town</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="town"
              placeholder="Town"
              value={formData.town}
            />
          </div>
          
          <div className="flex flex-col">
            <label className="referral-popup-label">House Number</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="house_number"
              placeholder="House Number"
              value={formData.house_number}
            />
          </div>
        </div>

        <div className="flex flex-col mb-4">
          <label className="referral-popup-label">Fayda Number</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="fayda_number"
            placeholder="Fayda Number"
            value={formData.fayda_number}
          />
        </div>

        {/* ID File Upload */}
        <div className="flex flex-col mb-4">
          <label className="referral-popup-label">ID Document</label>
          <div
            className="border-2 border-dashed border-gray-400 rounded-lg bg-[#f2f2f2] flex flex-col items-center justify-center p-6 cursor-pointer hover:border-blue-500 transition"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => document.getElementById("idFileInput")?.click()}
          >
            <input
              type="file"
              id="idFileInput"
              className="hidden"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
            />
            {formData.id_file ? (
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <svg
                    className="w-12 h-12 text-red-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <p className="text-green-600 font-medium">
                  {getFileName()}
                </p>
                {getFileSize() && (
                  <p className="text-gray-500 text-sm">
                    {getFileSize()}
                  </p>
                )}
                <p className="text-blue-500 text-sm mt-1">
                  Click to change file
                </p>
              </div>
            ) : (
              <div className="text-center">
                <svg
                  className="w-12 h-12 text-gray-400 mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <span className="text-gray-500 block mb-1">
                  Drag & Drop or Click to Upload ID Document
                </span>
                <span className="text-gray-400 text-sm">
                  Maximum file size: 10MB
                </span>
              </div>
            )}
          </div>
          {fileError && (
            <p className="text-red-500 text-sm mt-1">{fileError}</p>
          )}
        </div>

        {/* Action Buttons for Create Mode */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={loading}
            className={`flex-1 referral-form-button ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {loading ? "Creating..." : "Create User"}
          </button>
          
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition"
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;